import { Location, NgClass } from "@angular/common";
import { Component, OnInit, signal, ViewChild } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { ActivatedRoute, Router } from "@angular/router";
import { ProductInputComponent } from "@components/product-input/product-input.component";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import { IApiResponse } from "@models/api-response.model";
import { IProductRequestDto } from "@models/product-list.model";
import {
  LIVESTOCK_SEGMENT,
  livestockInputControls,
  OTHER_SEGMENT,
  otherProductInputControls,
  TANK_SEGMENT,
  tankInputControls,
} from "@pages/product/product-input/product-input-controls";
import { AppStateService, RequestService } from "@services/index";

@Component({
  selector: "fh-product-input-page",
  standalone: true,
  imports: [ProductInputComponent, NgClass, MatIconModule, RefreshButtonComponent],
  templateUrl: "./product-input-page.component.html",
  styleUrl: "./product-input-page.component.scss",
})
export class ProductInputPageComponent implements OnInit {
  // Form segments, not main categories: "Livestock" covers every livestock main
  // category (Freshwater and Marine), which the dropdown inside the form picks between.
  readonly categories = [
    { label: "Livestock", id: LIVESTOCK_SEGMENT },
    { label: "Tank", id: TANK_SEGMENT },
    { label: "Others", id: OTHER_SEGMENT },
  ];
  readonly livestockSegment = LIVESTOCK_SEGMENT;
  readonly tankSegment = TANK_SEGMENT;
  livestockControls = livestockInputControls;
  tankInputControls = tankInputControls;
  otherProductInputControls = otherProductInputControls;

  error = "";
  success = "";
  isSaving = false;

  productInput = {
    data: signal<IProductRequestDto>({} as IProductRequestDto),
    valid: signal<boolean>(false),
  };
  userId?: string;
  productId?: number;

  categoryId = signal<number>(LIVESTOCK_SEGMENT);

  /**
   * Which form to show for an existing product, resolved by the child from the
   * category names. Undefined until it has loaded; for a new product the
   * segment buttons (categoryId) decide instead.
   */
  productCategorySegment?: number;

  /** The segment actually in effect right now. */
  activeSegment(): number {
    return this.productCategorySegment ?? this.categoryId();
  }

  // Only the child ProductInputComponent knows how to (re)fetch the existing product's
  // data — refresh here just re-triggers that same fetch rather than duplicating it.
  // Only one of the three category variants in the template exists at a time, so this
  // always resolves to the active one. Also used to reload the form after a save.
  @ViewChild(ProductInputComponent) productInputComponent?: ProductInputComponent;
  refreshProduct = () => this.productInputComponent?.loadExistingProduct();

  constructor(
    _router: Router,
    private requestService: RequestService,
    private route: ActivatedRoute,
    private location: Location,
    private appStateService: AppStateService
  ) {}

  ngOnInit() {
    // The old x-access-token/localStorage login model wrote this to localStorage on
    // login; now that auth state comes from the session cookie, read it from
    // AppStateService instead (populated by AuthGaurd's live session check).
    this.userId = String(this.appStateService.currentUserId() ?? "");
    this.productId = parseInt(this.route.snapshot.paramMap.get("id") || "0", 10);
  }

  getProductCategorySegment(segment: number) {
    this.productCategorySegment = segment;
  }

  goBack(): void {
    this.location.back();
  }

  getFormTitle(): string {
    const segment = this.activeSegment();
    if (segment === LIVESTOCK_SEGMENT) return "Product Input (Live Stock)";
    if (segment === TANK_SEGMENT) return "Product Input (Tank)";
    return "Product Input (Other)";
  }

  saveProduct() {
    // Prevent double-submit (duplicate products) from rapid clicks while a save is in flight.
    if (this.isSaving) return;

    const data = this.productInput.data();

    const { images, videos, ...rest } = data;
    const requestBody = { ...rest } as any;

    requestBody.images = images.map((img) => {
      if (img.state === "add") {
        return img;
      }
      delete img.base64;
      return img;
    });

    requestBody.videos = videos
      .filter((video) => video.state === "delete")
      .map((video) => {
        delete video.base64;
        return video;
      });

    const saveVideos = (productId: string) => {
      const fd = new FormData();
      const videosToUpload = videos.filter((video) => video.state === "add");

      return new Promise<boolean>((resolve) => {
        if (videosToUpload.length > 0) {
          videosToUpload.forEach((video) => {
            fd.append(`video_${Date.now()}`, video.file as unknown as File);
          });
          fd.append("productId", productId);

          // Resolve on failure too. The save now waits on this before refreshing
          // the form, so a promise that only settles on success would leave the
          // page stuck mid-save whenever an upload failed.
          this.requestService.put("/api/v1/product-videos", fd).subscribe({
            // The backend answers 200 with status false when the upload failed.
            next: (res) => resolve((res as { status?: boolean } | null)?.status === true),
            error: () => resolve(false),
          });
        } else {
          resolve(true);
        }
      });
    };

    requestBody.vendor_id = Number(this.appStateService.currentUserId());

    // Only now: a throw while building the request above leaves Save usable.
    this.isSaving = true;
    this.requestService
      .post<IApiResponse>(`/api/v2/product?productId=${this.productId}`, requestBody)
      .subscribe({
        next: async (res) => {
          if (res.status === true) {
            const savedProductId = Number(res.data?.id) || this.productId!;

            // Wait for the uploads to land before refreshing below, or the
            // reloaded form would come back without the videos just added.
            const videosSaved = await saveVideos(String(savedProductId));

            this.success = videosSaved
              ? "Record saved successfully."
              : "Saved, but the video upload failed - try adding it again.";
            setTimeout(() => {
              this.success = "";
            }, 5000);

            // Stay on this page after saving rather than jumping to the product list.
            // A brand-new product (productId was 0/unset) just got assigned a real id --
            // fix the URL to match so a page refresh (or a second Save) edits it instead
            // of silently creating another product. replaceState avoids a real Angular
            // navigation, so the in-progress edit state here isn't touched.
            if (!this.productId && savedProductId) {
              this.productId = savedProductId;
              this.location.replaceState(`/product_input/${savedProductId}`);
            }

            // Rebuild the form from what was stored. Images and videos stay flagged
            // "add" in the in-memory form after being uploaded, so saving a second
            // time would upload every one of them again.
            // Save stays off until then: the form still holds the new options
            // without their ids, and a second Save would store them again.
            await this.productInputComponent?.reload(savedProductId);
            this.isSaving = false;
          } else {
            this.isSaving = false;
            // Surface actual Zod validation details when available
            if (Array.isArray(res.data) && res.data.length > 0) {
              this.error = (res.data as { path: string; message: string }[])
                .map((e) => (e.path ? `${e.path}: ${e.message}` : e.message))
                .join(" · ");
            } else {
              this.error = res.message;
            }
            setTimeout(() => {
              this.error = "";
            }, 5000);
          }
        },
        error: (err) => {
          this.isSaving = false;
          // A payload-too-large or gateway-timeout response has no JSON body — surface
          // whatever detail exists rather than a message that hides what actually happened.
          if (err?.status === 413) {
            this.error =
              "One or more images are too large to upload. Try removing a photo and saving again.";
          } else if (err?.error?.message) {
            this.error = err.error.message;
          } else if (err?.status) {
            this.error = `Save failed (HTTP ${err.status}${err.statusText ? ` ${err.statusText}` : ""}). Please try again.`;
          } else {
            this.error = "An unexpected error occurred. Please try again.";
          }
          setTimeout(() => {
            this.error = "";
          }, 5000);
        },
      });
  }
}
